import type { EntityCategory } from '@dealstack/shared/lib/dealstackAi/entities';

/** Line codes that make a row `invalid` (discard-only). */
export const HARD_REASON_CODES = [
	'holder_missing',
	'units_missing',
	'security_missing',
	'seller_missing',
	'units_not_positive',
] as const;
export type HardReasonCode = (typeof HARD_REASON_CODES)[number];

/** Line codes that send a row to review. */
export const SOFT_REASON_CODES = [
	'near_existing',
	'cost_mismatch',
	'number_not_verbatim',
	'quote_unverified',
	'image_only_evidence',
	'type_unknown',
	'loan_terms',
	'nominee_roles',
	'disposal_unfolded',
	'quote_not_verbatim',
	'document_not_evidence',
] as const;
export type SoftReasonCode = (typeof SOFT_REASON_CODES)[number];

/** Row codes derived by the review pipeline. */
const ROW_REASON_CODES = [
	/** The date's quote and page are the document date's. */
	'date_assumed',
	/** No full date anywhere; cleared when a sibling or the reviewer supplies one. */
	'date_missing',
	/** The row's own document prints a full date (effective or document) that is not the row's date. */
	'dates_differ',
	/** A date read off the document's e-signature certificate was dropped: a signing timestamp never dates a row. */
	'date_signing_stamp',
	/** Lifted from unanimous siblings. */
	'date_from_sibling',
	/** The reviewer entered the row date. */
	'date_entered',
	'conversion_target_missing',
	'fx_at_document_date',
	'fx_no_date',
	/** The reviewer replaced the market rate. */
	'fx_overridden',
	/** No market rate for the currency. */
	'fx_rate_missing',
	'currency_ambiguous',
	/** An entity on the row has no resolution or intent yet; the row's blockers name it. */
	'entity_unresolved',
	'entity_removed',
	'issuer_inferred',
	/** A collapsed line differs on a field. */
	'duplicate_disagrees',
	/** A person on the row is pending. */
	'holder_pending',
	/** Identical ledger rows; the reviewer attaches to one. */
	'ledger_twins',
	/** Two or more ledger rows that are different events fit the row equally; the reviewer names one. */
	'ledger_exact_tie',
	/** Another open row has the row's duplicate key inside the window; nothing collapses. */
	'likely_duplicate',
	/** A certificate or tax election does not state the event. */
	'type_unconfirmed',
	/** Named by a blocked confirm's fold check. */
	'fold_invalid',
	/** A line of a one-unit shape printing no units, and no convention confirmed for its deal. */
	'convention_unconfirmed',
	/** A transfer of part of a one-unit interest, which the platform cannot book. */
	'partial_interest_transfer',
	/** The create would split legal and beneficial title into two rows for a bought-for person. */
	'nominee_split_title',
	/** The vehicle-level copy of an issuer-level nominee row of the same document. */
	'nominee_vehicle_row',
	/** The row moves units a vehicle holds that no beneficiary accounts for (the nominee sum invariant). */
	'nominee_unattributed',
	/** The row, or its document, waits on information from the customer; it stays open and out of every confirm. */
	'awaiting_information',
	/**
	 * The row writes its cost and carries none, printed or entered; it stays staged until a cost is entered, since the
	 * platform keeps no transaction without a cost and a 0 would be a placeholder.
	 */
	'cost_missing',
] as const;
export type RowReasonCode = (typeof ROW_REASON_CODES)[number];

export const REASON_CODES = [
	...HARD_REASON_CODES,
	...SOFT_REASON_CODES,
	...ROW_REASON_CODES,
] as const;
export type ReasonCode = HardReasonCode | SoftReasonCode | RowReasonCode;

export const REASON_SEVERITIES = ['hard', 'soft', 'blocking', 'info'] as const;
export type ReasonSeverity = (typeof REASON_SEVERITIES)[number];

/** One reason on a row. Codes only: the UI maps each to its copy. */
export interface RowReason {
	code: ReasonCode;
	severity: ReasonSeverity;
	/** Set when the fix is a resolution, not a discard. */
	resolveIn: EntityCategory | null;
}

/** Why a row cannot be selected when no row reason names it. */
export const SELECT_BLOCK_CODES = [
	/** A `potential_existing` row waits for merge, replace or create. */
	'ledger_decision_required',
	/** In a running confirm. */
	'row_confirming',
	/** Already in the ledger. */
	'row_written',
	'row_discarded',
] as const;
export type SelectBlockCode = (typeof SELECT_BLOCK_CODES)[number];

/** `StagedTransaction.selectDisabledReason`: a row reason or a select-block code. */
export type SelectDisabledReason = ReasonCode | SelectBlockCode;
